// Precondition: Component receives pricing data via 'data' attribute as JSON string
// Postcondition: Renders an interactive pricing table with tier comparison

interface PricingTier {
  name: string;
  price: string | number;
  period?: string;
  description: string;
  features: string[];
  highlighted?: boolean;
  cta?: {
    text: string;
    action?: string;
    method?: "get" | "post";
  };
}

class PricingTable extends HTMLElement {
  private data: PricingTier[] = [];

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  static get observedAttributes() {
    return ["data"];
  }

  connectedCallback(): void {
    this.parseData();
    this.render();
  }

  attributeChangedCallback(
    name: string,
    oldValue: string,
    newValue: string,
  ): void {
    if (name === "data" && oldValue !== newValue && this.shadowRoot) {
      this.parseData();
      this.render();
    }
  }

  private parseData(): void {
    const dataAttr = this.getAttribute("data");
    if (dataAttr) {
      try {
        this.data = JSON.parse(dataAttr);
      } catch (e) {
        console.error("Failed to parse pricing data:", e);
        this.data = [];
      }
    }
  }

  private render(): void {
    const style = document.createElement("style");
    style.textContent = this.getStyles();

    const template = document.createElement("template");
    template.innerHTML = this.getTemplate();

    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = "";
      this.shadowRoot.appendChild(style);
      this.shadowRoot.appendChild(template.content.cloneNode(true));
      this.bindPostButtons();
    }
  }

  private getStyles(): string {
    return `
      :host {
        display: block;
      }

      /* Entrance animation */
      @keyframes fadeSlideIn {
        from {
          opacity: 0;
          transform: translateY(8px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      .pricing-container {
        width: 100%;
      }

      .pricing-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 24px;
        margin-top: 48px;
        padding-top: 16px;
      }

      .pricing-card {
        display: flex;
        flex-direction: column;
        border: 1px solid var(--border, #e5e7eb);
        border-radius: var(--radius-md, 12px);
        padding: 32px;
        background: var(--input-bg, #ffffff);
        position: relative;
        will-change: transform;
        transition: transform 150ms ease-out, border-color 150ms ease-out;
        animation: fadeSlideIn 200ms ease-out backwards;
      }

      .pricing-card:nth-child(1) { animation-delay: 0ms; }
      .pricing-card:nth-child(2) { animation-delay: 50ms; }
      .pricing-card:nth-child(3) { animation-delay: 100ms; }
      .pricing-card:nth-child(4) { animation-delay: 150ms; }

      .pricing-card:hover {
        border-color: var(--primary, #e0524d);
        transform: translateY(-2px);
      }

      .pricing-card.highlighted {
        border-color: var(--primary, #e0524d);
        box-shadow: 0 8px 24px rgba(224, 82, 77, 0.15);
        transform: translateY(-4px);
      }

      .pricing-card.highlighted:hover {
        transform: translateY(-6px);
      }

      .popular-badge {
        position: absolute;
        top: -14px;
        left: 50%;
        transform: translateX(-50%);
        background: var(--primary, #e0524d);
        color: var(--primary-text, #ffffff);
        padding: 6px 14px;
        border-radius: var(--radius-sm, 6px);
        font: 600 12px/1 var(--font-sans, Inter, system-ui, sans-serif);
        letter-spacing: 0.04em;
        text-transform: uppercase;
        white-space: nowrap;
        box-shadow: 0 2px 8px rgba(224, 82, 77, 0.3);
      }

      .tier-name {
        font-size: 24px;
        font-weight: 600;
        color: var(--heading, #111827);
        margin-bottom: 12px;
        font-family: "Space Grotesk", sans-serif;
        letter-spacing: -0.01em;
      }

      .tier-description {
        color: var(--muted, #6b7280);
        font-size: 14px;
        margin-bottom: var(--space-md, 24px);
        line-height: 1.5;
      }

      .tier-price {
        display: flex;
        align-items: baseline;
        margin-bottom: 16px;
      }

      .price-amount {
        font-size: 32px;
        font-weight: 700;
        color: var(--heading, #111827);
        font-family: "Space Grotesk", sans-serif;
        letter-spacing: -0.02em;
      }

      .price-period {
        color: var(--muted, #6b7280);
        font-size: 16px;
        font-weight: 500;
        margin-left: 4px;
      }

      .features-list {
        flex: 1;
        margin: 24px 0;
        list-style: none;
        padding: 0;
      }

      .features-list li {
        display: flex;
        align-items: center;
        padding: 12px 0;
        color: var(--text, #1c1c1e);
        font-size: 14px;
        border-bottom: 1px solid var(--border, #e5e7eb);
      }

      .features-list li:last-child {
        border-bottom: none;
      }

      .feature-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        margin-right: 12px;
        color: var(--primary, #e0524d);
        font-weight: bold;
        flex-shrink: 0;
      }

      .cta-wrapper {
        margin-top: auto;
      }

      .cta-wrapper ft-button {
        width: 100%;
      }

      /* Reduced motion support */
      @media (prefers-reduced-motion: reduce) {
        .pricing-card {
          animation: none;
          transition: none;
        }
      }

      @media (max-width: 768px) {
        .pricing-grid {
          grid-template-columns: 1fr;
        }

        .pricing-card.highlighted {
          transform: translateY(0);
        }

        .pricing-card.highlighted:hover {
          transform: translateY(-2px);
        }
      }
    `;
  }

  private getTemplate(): string {
    if (this.data.length === 0) {
      return `
        <div class="pricing-container">
          <p style="color: var(--muted, #6b7280); text-align: center; padding: 40px 0;">
            No pricing data available
          </p>
        </div>
      `;
    }

    const cardsHtml = this.data.map((tier) => this.renderCard(tier)).join("");

    return `
      <div class="pricing-container">
        <div class="pricing-grid">
          ${cardsHtml}
        </div>
      </div>
    `;
  }

  private bindPostButtons(): void {
    if (!this.shadowRoot) return;
    const buttons = this.shadowRoot.querySelectorAll("[data-post-action]");
    buttons.forEach((btn) => {
      btn.addEventListener("click", async (e: Event) => {
        e.preventDefault();
        const action = (btn as HTMLElement).dataset.postAction;
        if (!action) return;
        (btn as HTMLElement).setAttribute("disabled", "");
        try {
          const res = await fetch(action, {
            method: "POST",
            headers: { "X-Requested-With": "fetch" },
          });
          if (!res.ok) throw new Error("Request failed");
          const data = (await res.json()) as { url: string };
          if (
            !data.url.startsWith("https://checkout.stripe.com/") &&
            !data.url.startsWith("https://billing.stripe.com/")
          ) {
            throw new Error("Invalid redirect URL");
          }
          window.location.href = data.url;
        } catch {
          (btn as HTMLElement).removeAttribute("disabled");
        }
      });
    });
  }

  private renderCard(tier: PricingTier): string {
    const highlightedClass = tier.highlighted ? "highlighted" : "";
    const cardId = `tier-${tier.name.toLowerCase().replace(/\s+/g, "-")}`;
    const priceText = tier.period
      ? `${tier.price} per ${tier.period}`
      : tier.price;
    const period = tier.period
      ? `<span class="price-period">/${tier.period}</span>`
      : "";
    const buttonType = tier.highlighted ? "primary" : "secondary";
    const badge = tier.highlighted
      ? `<span class="popular-badge" role="status" aria-label="Most popular plan">Most popular</span>`
      : "";

    let ctaButton = "";
    if (tier.cta) {
      const action = tier.cta.action || "/";
      if (tier.cta.method === "post") {
        ctaButton = `<div class="cta-wrapper"><ft-button type="${buttonType}" data-post-action="${action}" label="${tier.cta.text} - ${tier.name} plan">${tier.cta.text}</ft-button></div>`;
      } else {
        ctaButton = `<div class="cta-wrapper"><ft-button type="${buttonType}" href="${action}" label="${tier.cta.text} - ${tier.name} plan">${tier.cta.text}</ft-button></div>`;
      }
    }

    const features = tier.features
      .map(
        (feature) =>
          `<li><span class="feature-icon" aria-hidden="true">✓</span>${feature}</li>`,
      )
      .join("");

    return `
      <article class="pricing-card ${highlightedClass}" aria-labelledby="${cardId}-name">
        ${badge}
        <h3 class="tier-name" id="${cardId}-name">${tier.name}</h3>
        <p class="tier-description">${tier.description}</p>
        <div class="tier-price" aria-label="Price: ${priceText}">
          <span class="price-amount">${tier.price}</span>
          ${period}
        </div>
        <ul class="features-list" aria-label="Features included in ${tier.name}">
          ${features}
        </ul>
        ${ctaButton}
      </article>
    `;
  }
}

// Register custom element

customElements.define("ft-pricing-table", PricingTable);
